import type { DimensionKey } from '@hub/bearing-hub/common/types';
import {
  IbhIcnProcedure,
  IbhProcedure,
  IbhProductionMethods,
  IbhShape,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';

export const dimensionKey = {
  b1: 'b1',
  b2: 'b2',
  b3: 'b3',
  d1: 'd1',
  d2: 'd2',
  d3: 'd3',
  d4: 'd4',
  d5: 'd5',
  d6: 'd6',
  d7: 'd7',
  r: 'r',
  slotAngle: 'slotAngle',
  sb: 'sb',
  b2d3: 'b2d3',
  b2d4: 'b2d4',
  h: 'h',
  t: 't',
  frd: 'frd',
  frW: 'frW',
  frT: 'frT',
} as const;

export const bevelKey = {
  'f1.length': 'f1.length',
  'f2.length': 'f2.length',
  'f3.length': 'f3.length',
  'f4.length': 'f4.length',
  'f1.angle': 'f1.angle',
  'f2.angle': 'f2.angle',
  'f3.angle': 'f3.angle',
  'f4.angle': 'f4.angle',
} as const;

export const lifetimeConfigParams = {
  acceptableWear: 'acceptableWear',
  activityMinutesPerHour: 'activityMinutesPerHour',
  environment: 'environment',
  typeOfOperation: 'typeOfOperation',
  intermittentParams: 'intermittentParams',
  onTime: 'onTime',
  offTime: 'offTime',
  load: 'load',
  surfacePressure: 'surfacePressure',
  force: 'force',
  linearFrequency: 'linearFrequency',
  linearStrokeLength: 'linearStrokeLength',
  oppositeMaterialId: 'oppositeMaterialId',
  pivotingAngle: 'pivotingAngle',
  pivotingFrequency: 'pivotingFrequency',
  rotationRevolutions: 'rotationRevolutions',
  temperature: 'temperature',
  typeOfMovement: 'typeOfMovement',
  temporalLinearPart: 'temporalLinearPart',
  temporalTypeOfMovement: 'temporalTypeOfMovement',
} as const;

export const ShapeDimensionsDefinition = {
  [IbhShape.S]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1],
  [IbhShape.F]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2],
  [IbhShape.T]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1],
  [IbhShape.FL]: [
    dimensionKey.d1,
    dimensionKey.d2,
    dimensionKey.d3,
    dimensionKey.d4,
    dimensionKey.d5,
    dimensionKey.d6,
    dimensionKey.d7,
    dimensionKey.b1,
    dimensionKey.b2,
    dimensionKey.b3,
    dimensionKey.r,
  ],
  [IbhShape.PR]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1, dimensionKey.slotAngle, dimensionKey.b2],
  [IbhShape.EC]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b2, dimensionKey.b3, dimensionKey.sb],

  [IbhShape.C]: [
    dimensionKey.d1,
    dimensionKey.d2,
    dimensionKey.d3,
    dimensionKey.d4,
    dimensionKey.b2d3,
    dimensionKey.b2d4,
    dimensionKey.b3,
  ],
  [IbhShape.Y]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2],
  [IbhShape.YK]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2],
  [IbhShape.VFL]: [
    dimensionKey.d1,
    dimensionKey.d2,
    dimensionKey.d3,
    dimensionKey.d4,
    dimensionKey.d5,
    dimensionKey.d6,
    dimensionKey.d7,
    dimensionKey.b1,
    dimensionKey.b2,
    dimensionKey.b3,
    dimensionKey.r,
  ],
  [IbhShape.TE]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.h, dimensionKey.t],
  [IbhShape.D]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1, dimensionKey.b2],
  [IbhShape.VF]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2, dimensionKey.b3],
  [IbhShape.K]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2, dimensionKey.b3],
  [IbhShape.VS]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1, dimensionKey.b3],
  [IbhShape.FSG]: [
    dimensionKey.d1,
    dimensionKey.d2,
    dimensionKey.d3,
    dimensionKey.b1,
    dimensionKey.b2,
    dimensionKey.frd,
    dimensionKey.frW,
    dimensionKey.frT,
  ],
};

export const ShapeDimensionsDefinitionForm: { [key in IbhShape]: DimensionKey[] } = {
  ...ShapeDimensionsDefinition,
  [IbhShape.FL]: [dimensionKey.d1], // This shape has a limited number of fields
  [IbhShape.Y]: [dimensionKey.d1, dimensionKey.d2], // This shape has a limited number of fields
  [IbhShape.VFL]: [dimensionKey.d1], // This shape has a limited number of fields
  [IbhShape.TE]: [dimensionKey.d1], // This shape has a limited number of fields
  [IbhShape.D]: [dimensionKey.d1, dimensionKey.d2], // This shape has a limited number of fields
  [IbhShape.VF]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2], // This shape has a limited number of fields
  [IbhShape.K]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1], // This shape has a limited number of fields
  [IbhShape.VS]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1], // This shape has a limited number of fields
  [IbhShape.FSG]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2], // This shape has a limited number of fields
};

export const ShapeDimensionsDefinitionUsp: { [key in IbhShape]: DimensionKey[] } = {
  [IbhShape.S]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1],
  [IbhShape.F]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2],
  [IbhShape.FL]: [dimensionKey.d1],
  [IbhShape.VFL]: [dimensionKey.d1],
  [IbhShape.Y]: [dimensionKey.d1, dimensionKey.b1, dimensionKey.d3],
  [IbhShape.YK]: [dimensionKey.d1, dimensionKey.b1, dimensionKey.d3],
  [IbhShape.C]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b3],
  [IbhShape.D]: [dimensionKey.d1, dimensionKey.b1, dimensionKey.d3],
  [IbhShape.T]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1],
  [IbhShape.PR]: [dimensionKey.d1, dimensionKey.d2],
  [IbhShape.TE]: [dimensionKey.d1, dimensionKey.d2],
  [IbhShape.EC]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b3],
  [IbhShape.VF]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1],
  [IbhShape.K]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1],
  [IbhShape.VS]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.b1],
  [IbhShape.FSG]: [dimensionKey.d1, dimensionKey.d2, dimensionKey.d3, dimensionKey.b1, dimensionKey.b2],
};

export const spatialTypeOfMovement = {
  linear: 'linear',
  pivoting: 'pivoting',
  rotation: 'rotation',
  linearAndRotation: 'linear and rotation',
  linearAndPivoting: 'linear and pivoting',
} as const;

export const bearingLifetimeEnvironment = {
  Dry: 'dry',
  UnderWater: 'underWater',
} as const;

export const bearingProductionMethods = {
  FAST_LINE: 'FAST_LINE',
  MECHANICAL_REPROCESSING: 'MECHANICAL_REPROCESSING',
  MOLD_INJECTION: 'MOLD_INJECTION',
  SEMI_FINISHED: 'SEMI_FINISHED',
  THREE_DIMENSIONAL_PRINT: 'THREE_DIMENSIONAL_PRINT',
  WOUND: 'WOUND',
} as const;

export const widensItemsType = {
  drawing: 'drawing',
  image: 'image',
};

export const fileType = {
  drawing: 'drawing',
  report: 'report',
  stl: 'stl',
  stp: 'stp',
} as const;

export const BEARINGHUB_NODEID_OVERWRITES: {
  default: number;
} = {
  default: 300716,
};

export const QUANTITY_LIMIT = 14999;
export const BEARINGHUB_QUANTITYRANGES = [1, 10, 25, 50, 100, 200, 500, 1000, 2500, 5000, 10000, QUANTITY_LIMIT];
export const DEFAULT_QUANTITY = 100;
export const MAX_PRICE_QUANTITY = 15000;

export const printProcedures: IbhProcedure[] = [IbhProcedure.DLP, IbhProcedure.SLS, IbhProcedure.FDM];
export const cncProcedures: IbhIcnProcedure[] = [IbhIcnProcedure.CNCEXPRESS, IbhIcnProcedure.CNC];

export const methodSpecificProcedures: Record<IbhProductionMethods, IbhProcedure[] | IbhIcnProcedure[]> = {
  [IbhProductionMethods.THREEDIMENSIONALPRINT]: printProcedures,
  [IbhProductionMethods.MECHANICALREPROCESSING]: [IbhProcedure.CNC],
  [IbhProductionMethods.WOUND]: [IbhProcedure.WOUND],
  [IbhProductionMethods.MOLDINJECTION]: [IbhProcedure.IM],
  [IbhProductionMethods.FASTLINE]: [IbhProcedure.IM],
  [IbhProductionMethods.SEMIFINISHED]: [IbhIcnProcedure.CNC, IbhIcnProcedure.CNCEXPRESS],
};
